import { useState } from 'react';
import { useMutation, useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import {
  BillingRow, BillingHistoryDoc, getBilling, postBillingAction, whatsappChargeLink,
  getBillingHistoryMonths, getBillingHistoryMonth,
} from '../../api/gestor';
import { Icon, Modal, useToast } from './ui';

type Filter = 'todos' | 'pendente' | 'pago' | 'manutencao' | 'implantacao';

function parseValor(v: string): number {
  const n = parseFloat(String(v).replace(/[^\d.,]/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

const fmt = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
// versao curta p/ os KPIs (sem centavos — cabe melhor no card)
const fmtK = (n: number) =>
  (n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const OK_STATUS = ['PAGO', 'MANUTENCAO', 'IMPLANTACAO'];

export function BillingTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>('todos');
  const [modal, setModal] = useState<{
    kind: 'baixa' | 'desconto' | 'valor' | 'manutencao' | 'implantacao'; row: BillingRow;
  } | null>(null);
  const [value, setValue] = useState('');
  // na baixa: a qual fatura ela se refere ('atual', um mês fechado 'YYYY-MM',
  // ou '__all__' para quitar tudo)
  const [baixaComp, setBaixaComp] = useState<string>('atual');

  const [month, setMonth] = useState<'atual' | string>('atual');

  const { data: liveRows, isLoading, error } = useQuery({
    queryKey: ['billing'],
    queryFn: getBilling,
    staleTime: 60 * 1000,
  });
  const { data: historyMonths } = useQuery({
    queryKey: ['billing-months'],
    queryFn: getBillingHistoryMonths,
    staleTime: 10 * 60 * 1000,
  });
  // carrega TODOS os fechamentos: preciso deles pra saber quais meses cada
  // cliente ainda deve (selo "deve" + opções de baixa retroativa)
  const historyResults = useQueries({
    queries: (historyMonths || []).map((m) => ({
      queryKey: ['billing-history', m],
      queryFn: () => getBillingHistoryMonth(m),
      enabled: !!m,
      staleTime: Infinity,
    })),
  });
  const historyDocs: Record<string, BillingHistoryDoc> = {};
  (historyMonths || []).forEach((m, i) => {
    const d = historyResults[i]?.data;
    if (d) historyDocs[m] = d;
  });

  // meses fechados em aberto por cliente (mais antigo primeiro). Quem foi pago
  // com atraso já virou PAGO no snapshot, então sai daqui sozinho.
  const overdueByClient = new Map<string, string[]>();
  [...(historyMonths || [])].sort().forEach((m) => {
    const doc = historyDocs[m];
    if (!doc) return;
    (doc.rows || []).forEach((r: BillingRow) => {
      if (r.nome && !OK_STATUS.includes(r.status)) {
        const list = overdueByClient.get(r.nome) || [];
        list.push(m);
        overdueByClient.set(r.nome, list);
      }
    });
  });
  const liveByName = new Map((liveRows || []).map((r) => [r.nome, r]));

  const isFuture = /^\d{4}-\d{2}$/.test(month) &&
    month > new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 7);
  const isPast = month !== 'atual' && !isFuture;
  const viewingHistory = isPast;
  const liveView = month === 'atual';
  // futuro: previsão = clientes ativos como "a receber"; histórico = snapshot
  const rows = isFuture
    ? (liveRows || []).filter((r) => r.nome && !['MANUTENCAO', 'IMPLANTACAO'].includes(r.status))
        .map((r) => ({ ...r, status: 'PENDENTE' }))
    : isPast ? historyDocs[month]?.rows : liveRows;

  const action = useMutation({
    mutationFn: ({ rowIndex, act, val, comp }:
      { rowIndex: number; act: string; val?: string; comp?: string | string[] }) =>
      postBillingAction(rowIndex, act, val, comp),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      // baixa retroativa mexe nos snapshots: recarrega histórico e meses
      queryClient.invalidateQueries({ queryKey: ['billing-history'] });
      queryClient.invalidateQueries({ queryKey: ['billing-months'] });
      toast(
        vars.act === 'baixa'
          ? (vars.comp ? `Baixa registrada — referente a ${vars.comp}` : 'Baixa registrada (1 fatura abatida)')
          : vars.act === 'quitar' ? 'Quitado — todas as faturas baixadas'
          : vars.act === 'desbloquear' ? 'Quadra liberada na busca do site'
          : vars.act === 'desconto' ? 'Desconto atualizado'
            : vars.act === 'reabrir' ? 'Cobrança reaberta'
              : vars.act === 'manutencao' ? 'Cliente em manutenção — fora das cobranças'
                : 'Valor atualizado');
      setModal(null);
      setValue('');
      setBaixaComp('atual');
    },
    onError: (e: any) => toast(`Erro: ${e.message}`, 'warn'),
  });

  if (isLoading) return <div className="loading-page"><span className="spinner" /> carregando planilha…</div>;
  if (error) return <div className="report-empty">Erro ao ler planilha: {(error as any).message}</div>;

  const all = rows || [];
  const statusOf = (r: BillingRow) =>
    r.status === 'PAGO' ? 'pago'
      : r.status === 'MANUTENCAO' ? 'manutencao'
        : r.status === 'IMPLANTACAO' ? 'implantacao' : 'pendente';
  // espelha a regra do backend (billing.js isRowBlocked): inadimplente E
  // (3+ avisos OU 2+ faturas) E sem liberação manual.
  const inadimplente = (r: BillingRow) =>
    !['PAGO', 'MANUTENCAO', 'IMPLANTACAO'].includes(r.status);
  const isBlocked = (r: BillingRow) =>
    inadimplente(r) && !r.liberado &&
    ((r.avisosEnviados ?? 0) >= 3 || (r.faturasAbertas ?? 0) >= 2);
  const faturasDe = (r: BillingRow) => r.faturasAbertas ?? 0;
  const valorLiquido = (r: BillingRow) =>
    parseValor(r.valor) * (1 - parseValor(r.desconto) / 100);
  const overdueOf = (r: BillingRow) => overdueByClient.get(r.nome) || [];
  // itens em aberto do modal de baixa: mês atual (se pendente) + meses fechados
  // em atraso. No histórico, só o mês visualizado.
  const baixaItems: { key: string; label: string }[] = modal?.kind === 'baixa'
    ? (!liveView
        ? [{ key: month, label: `${month} (fechado)` }]
        : [
            ...(statusOf(modal.row) === 'pendente' ? [{ key: 'atual', label: 'Mês atual' }] : []),
            ...overdueOf(modal.row).map((m) => ({ key: m, label: `${m} (fechado)` })),
          ])
    : [];
  // o contador da planilha (FaturasAbertas) pode acusar mais faturas do que os
  // meses que sei atribuir — nesse caso ainda ofereço "quitar todas"
  const baixaOpenCount = modal?.kind === 'baixa'
    ? Math.max(baixaItems.length, liveView ? faturasDe(modal.row) : 1) : 0;
  const baixaHasChoice = baixaOpenCount > 1;
  // selos de bloqueio/faturas só fazem sentido na planilha viva (não em
  // histórico nem em projeção futura)
  const filtered = all.filter((r) => (filter === 'todos' ? true : statusOf(r) === filter));
  const sum = (f: (r: BillingRow) => boolean) =>
    all.filter(f).reduce((s, r) => s + parseValor(r.valor), 0);
  const totalReceber = sum((r) => statusOf(r) === 'pendente');
  const totalRecebido = sum((r) => statusOf(r) === 'pago');
  const totalManutencao = sum((r) => statusOf(r) === 'manutencao');
  const totalImplantacao = sum((r) => statusOf(r) === 'implantacao');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Pagamentos</h1>
          <div className="sub">
            {isFuture
              ? <>Previsão de <b>{month}</b> — recebimentos esperados dos clientes ativos (somente leitura)</>
              : isPast
                ? <>Fechamento de <b>{month}</b> — quem ficou devendo pode receber baixa aqui (paga com atraso)</>
                : 'Planilha viva do bot de cobrança — alterações aqui valem para o bot também'}
          </div>
        </div>
        <div className="pay-filters">
          {([['todos', 'Todos'], ['pendente', 'Pendentes'], ['pago', 'Pagos'],
            ['manutencao', 'Manutenção'], ['implantacao', 'Implantação']] as [Filter, string][]).map(([f, label]) => (
            <button key={f}
              className={`chip ${filter === f
                ? `active ${f === 'pago' ? 'green' : f === 'pendente' ? 'red' : f === 'manutencao' ? 'yellow' : ''}` : ''}`}
              onClick={() => setFilter(f)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="month-chips">
        {(historyMonths || []).map((m) => (
          <button key={m} className={`chip ${month === m ? 'active' : ''}`}
            onClick={() => setMonth(m)}>{m}</button>
        ))}
        <button className={`chip ${month === 'atual' ? 'active' : ''}`}
          onClick={() => setMonth('atual')}>Mês atual</button>
        {(() => {
          // meses futuros: previsão de recebimento (clientes ativos)
          const base = new Date(Date.now() - 3 * 3600 * 1000);
          const futuros = Array.from({ length: 6 }, (_, i) => {
            const d = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + i + 1, 1));
            return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
          });
          return futuros.map((m) => (
            <button key={m} className={`chip ${month === m ? 'active' : ''}`}
              onClick={() => setMonth(m)}>{m} →</button>
          ));
        })()}
      </div>

      <div className="kpis" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
        <div className="kpi alert">
          <div className="klabel"><Icon name="money" size={12} /> A receber</div>
          <div className="kval">{fmtK(totalReceber)}</div>
          <div className="kfoot"><b>{all.filter((r) => statusOf(r) === 'pendente').length}</b> cobranças pendentes</div>
        </div>
        <div className="kpi good">
          <div className="klabel"><Icon name="check" size={12} /> Recebido</div>
          <div className="kval">{fmtK(totalRecebido)}</div>
          <div className="kfoot"><b>{all.filter((r) => statusOf(r) === 'pago').length}</b> pagos este mês</div>
        </div>
        <div className="kpi maint">
          <div className="klabel"><Icon name="alert" size={12} /> Em manutenção</div>
          <div className="kval">{fmtK(totalManutencao)}</div>
          <div className="kfoot"><b>{all.filter((r) => statusOf(r) === 'manutencao').length}</b> clientes parados — valor que deixo de receber</div>
        </div>
        <div className="kpi impl">
          <div className="klabel"><Icon name="chip" size={12} /> Aguardando implantação</div>
          <div className="kval">{fmtK(totalImplantacao)}</div>
          <div className="kfoot"><b>{all.filter((r) => statusOf(r) === 'implantacao').length}</b> compradas, ainda não instaladas</div>
        </div>
        <div className="kpi">
          <div className="klabel"><Icon name="doc" size={12} /> Clientes</div>
          <div className="kval">{all.length}</div>
          <div className="kfoot">na planilha de cobrança</div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 8, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="ptable ptable-lg">
            <thead>
              <tr>
                <th style={{ paddingLeft: 22 }}>Cliente / Arena</th>
                <th className="r">Valor</th>
                <th>Cobrança</th>
                <th>Status</th>
                <th className="r" style={{ paddingRight: 22 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const st = statusOf(row);
                const desconto = parseValor(row.desconto);
                const owed = liveView ? overdueOf(row) : [];
                return (
                  <tr key={row.rowIndex} className={st === 'manutencao' ? 'row-maint' : ''}>
                    <td style={{ paddingLeft: 22 }}>
                      <div className="cli">
                        {row.nome}
                        {liveView && isBlocked(row) && (
                          <span className="deve-badge" style={{ background: '#dc2626', color: '#fff' }}
                            title="Quadra bloqueada na busca do site por inadimplência">
                            🔒 bloqueado
                          </span>
                        )}
                        {liveView && faturasDe(row) >= 2 && (
                          <span className="deve-badge" title="Faturas em aberto acumuladas">
                            deve {faturasDe(row)} faturas
                          </span>
                        )}
                        {!viewingHistory && owed.length > 0 && (
                          <span className="deve-badge" title={`Fechamentos em aberto: ${owed.join(', ')}`}>
                            deve {owed[0]}{owed.length > 1 ? ` +${owed.length - 1}` : ''}
                          </span>
                        )}
                        {isPast && row.paid_late_at && (
                          <span className="deve-badge" style={{ background: '#16a34a', color: '#fff' }}
                            title="Pago depois do fechamento">
                            pago c/ atraso
                          </span>
                        )}
                      </div>
                      <div className="contato">{row.contato}</div>
                    </td>
                    <td className="r valor">
                      {fmt(parseValor(row.valor))}
                      {desconto > 0 && <span className="desc">-{row.desconto}% desconto</span>}
                      {liveView && faturasDe(row) >= 2 && (
                        <span className="desc" style={{ color: '#dc2626' }}>
                          total {fmt(valorLiquido(row) * faturasDe(row))} · {faturasDe(row)} faturas
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="cobr-dia">todo dia <b>{row.diaCobranca || '—'}</b></div>
                      {row.dataUltimoAviso && (
                        <div className="cobr-aviso">último aviso {row.dataUltimoAviso}</div>
                      )}
                    </td>
                    <td>
                      <span className={`tag-st ${st === 'pago' ? 'pago' : st === 'manutencao' ? 'manut' : st === 'implantacao' ? 'impl' : 'pendente'}`}>
                        <span className={`dot ${st === 'pago' ? 'ok' : st === 'pendente' ? 'bad' : 'warn'}`} />
                        {st === 'pago' ? 'Pago' : st === 'manutencao' ? 'Manutenção'
                          : st === 'implantacao' ? 'Implantação' : 'Pendente'}
                      </span>
                    </td>
                    <td className="r" style={{ paddingRight: 22 }}>
                      {isPast ? (
                        // fechamento: quem ficou pendente pode receber baixa
                        // retroativa (paga com atraso), desde que ainda exista
                        // na planilha viva
                        st !== 'pendente' ? (
                          <span className="contato">{row.paid_late_at ? 'pago' : 'fechado'}</span>
                        ) : !liveByName.get(row.nome) ? (
                          <span className="contato">fora da planilha</span>
                        ) : (
                          <div className="acoes">
                            <button className="iconbtn ok" title={`Dar baixa referente a ${month} (paga com atraso)`}
                              onClick={() => { setBaixaComp(month); setModal({ kind: 'baixa', row }); }}>
                              <Icon name="check" />
                            </button>
                            <a className="iconbtn wa" title="Cobrar no WhatsApp"
                              href={whatsappChargeLink(liveByName.get(row.nome)!)} target="_blank" rel="noreferrer">
                              <Icon name="wa" />
                            </a>
                          </div>
                        )
                      ) : isFuture ? (
                        <span className="contato">previsto</span>
                      ) : (
                      <div className="acoes">
                        {isBlocked(row) && (
                          <button className="iconbtn ok" title="Desbloquear na busca do site (liberação manual)"
                            onClick={() => action.mutate({ rowIndex: row.rowIndex, act: 'desbloquear' })}>
                            <Icon name="power" />
                          </button>
                        )}
                        {(st === 'pendente' || owed.length > 0) && (
                          <button className="iconbtn ok"
                            title={owed.length > 0 ? 'Dar baixa (escolher a qual mês se refere)' : 'Dar baixa'}
                            onClick={() => { setBaixaComp(owed[0] || 'atual'); setModal({ kind: 'baixa', row }); }}>
                            <Icon name="check" />
                          </button>
                        )}
                        {st !== 'pendente' && (
                          <button className="iconbtn" title="Reabrir cobrança (volta a Pendente)"
                            onClick={() => action.mutate({ rowIndex: row.rowIndex, act: 'reabrir' })}>
                            <Icon name="reset" />
                          </button>
                        )}
                        {st !== 'manutencao' && (
                          <button className="iconbtn warn" title="Colocar em manutenção (não cobra)"
                            onClick={() => setModal({ kind: 'manutencao', row })}>
                            <Icon name="alert" />
                          </button>
                        )}
                        {st !== 'implantacao' && (
                          <button className="iconbtn" title="Aguardando implantação (comprada, não instalada — não cobra)"
                            onClick={() => setModal({ kind: 'implantacao', row })}>
                            <Icon name="chip" />
                          </button>
                        )}
                        <button className="iconbtn warn" title="Dar desconto"
                          onClick={() => { setModal({ kind: 'desconto', row }); setValue(row.desconto || ''); }}>
                          <Icon name="percent" />
                        </button>
                        <button className="iconbtn" title="Alterar valor"
                          onClick={() => { setModal({ kind: 'valor', row }); setValue(String(parseValor(row.valor) || '')); }}>
                          <Icon name="money" />
                        </button>
                        <a className="iconbtn wa" title="Enviar cobrança no WhatsApp"
                          href={whatsappChargeLink(row)} target="_blank" rel="noreferrer">
                          <Icon name="wa" />
                        </a>
                      </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && (
                <tr><td colSpan={5} style={{ padding: 24, color: 'var(--ap-faint)' }}>
                  Nenhuma cobrança neste filtro.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        title={modal?.kind === 'baixa' ? `Dar baixa — ${modal.row.nome}`
          : modal?.kind === 'manutencao' ? `Manutenção — ${modal?.row.nome}`
            : modal?.kind === 'implantacao' ? `Aguardando implantação — ${modal?.row.nome}`
              : modal?.kind === 'desconto' ? `Desconto — ${modal?.row.nome}`
                : `Alterar valor — ${modal?.row.nome}`}
        desc={modal?.kind === 'baixa'
          ? (!liveView
              ? `Confirma que ${modal.row.nome} pagou a fatura de ${month} (com atraso). Some o selo de atraso e a receita passa a contar em ${month} nos Relatórios.`
              : baixaHasChoice
                ? 'Escolha a qual fatura esta baixa se refere. Cada baixa abate uma; "Quitar todas" zera o débito.'
                : `Marca StatusPagamento=PAGO na planilha (valor ${modal.row.valor}). Desbloqueia a busca.`)
          : modal?.kind === 'manutencao'
            ? 'Cliente fora do ar: o bot NÃO cobra e o valor sai do "A receber" (vai pro KPI Manutenção).'
            : modal?.kind === 'implantacao'
              ? 'Quadra comprada mas ainda não instalada: o bot NÃO cobra, não conta no "A receber" e entra no KPI de implantação (aqui e nos Relatórios). Quando ligar, use Reabrir.'
              : modal?.kind === 'desconto'
                ? 'Percentual de desconto aplicado pelo bot na próxima cobrança.'
                : 'Novo valor mensal da cobrança (atualiza a planilha do bot).'}
        open={!!modal}
        onClose={() => { setModal(null); setValue(''); setBaixaComp('atual'); }}
        busy={action.isPending}
        confirmText={modal?.kind === 'baixa'
          ? (baixaHasChoice && baixaComp === '__all__'
              ? `Quitar todas (${baixaOpenCount})` : 'Confirmar baixa')
          : modal?.kind === 'manutencao' ? 'Colocar em manutenção'
            : modal?.kind === 'implantacao' ? 'Marcar como implantação' : 'Salvar'}
        confirmClass={modal?.kind === 'manutencao' || modal?.kind === 'implantacao' ? 'btn-orange' : 'btn-green'}
        onConfirm={() => {
          if (!modal) return;
          if (modal.kind === 'baixa') {
            const sel = !baixaHasChoice ? (baixaItems[0]?.key ?? 'atual') : baixaComp;
            const rowIndex = liveView ? modal.row.rowIndex : liveByName.get(modal.row.nome)?.rowIndex;
            if (rowIndex == null) { toast('Cliente não está na planilha viva do bot', 'warn'); return; }
            if (sel === '__all__') {
              action.mutate({ rowIndex, act: 'quitar', comp: overdueOf(modal.row) });
            } else if (sel === 'atual') {
              action.mutate({ rowIndex, act: 'baixa' });
            } else {
              action.mutate({ rowIndex, act: 'baixa', comp: sel });
            }
            return;
          }
          action.mutate({
            rowIndex: modal.row.rowIndex,
            act: modal.kind,
            val: modal.kind === 'desconto' || modal.kind === 'valor' ? value : undefined,
          });
        }}>
        {modal?.kind === 'baixa' && baixaHasChoice && (
          <div style={{ display: 'grid', gap: 6 }}>
            {baixaItems.map((it) => (
              <label key={it.key} style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" name="baixacomp" checked={baixaComp === it.key}
                  onChange={() => setBaixaComp(it.key)} />
                {it.label}
              </label>
            ))}
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" name="baixacomp" checked={baixaComp === '__all__'}
                onChange={() => setBaixaComp('__all__')} />
              Quitar todas ({baixaOpenCount})
            </label>
          </div>
        )}
        {modal?.kind === 'desconto' && (<>
          <label>Desconto (%)</label>
          <input type="number" min={0} max={100} value={value}
            onChange={(e) => setValue(e.target.value)} placeholder="10" />
        </>)}
        {modal?.kind === 'valor' && (<>
          <label>Novo valor (R$)</label>
          <input type="number" min={0} value={value}
            onChange={(e) => setValue(e.target.value)} placeholder="450" />
        </>)}
      </Modal>
    </>
  );
}
